import React from "react";
import {
  Box,
  Dialog,
  FormHelperText,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CustomIcon from "../CustomIcon";
import CustomTag from "../CustomTag";

export const MAX_REMARK_LENGTH = 250;

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  // The dialog's own paper only. MuiPaper-root would also match every Paper
  // rendered inside the dialog -- an Alert is one -- and give it the dialog's
  // width, padding and shadow.
  "& .MuiDialog-paper": {
    borderRadius: 8,
    boxShadow: theme.shadows[5],
    width: "560px",
    maxWidth: "90%",
    padding: "24px",
  },
}));

export const StyledTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    fontSize: "12px",
    lineHeight: "24px",
    padding: 0,
    backgroundColor: "#FFFFFF",
    "& fieldset": {
      borderColor: "#E6E6E6",
      borderWidth: "1px",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#6D72F6",
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6D72F6",
      borderWidth: "1px",
    },
    "& input": {
      padding: "12px 16px",
      fontSize: "14px",
      lineHeight: "20px",
    },
    "& textarea": {
      padding: "8px 16px",
      fontSize: "14px",
      lineHeight: "20px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: "#A4B2C1",
      opacity: 1,
    },
  },
  "& .MuiList-root": {
    padding: 0,
  },
});

export const OptionItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "12px 16px",
  backgroundColor: "#F9F9F9",
  color: "#494949",
  borderRadius: "8px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#F7F7FF",
    color: "#6D72F6",
  },
});

export const BackButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  gap: "8px",
  marginBottom: "16px",
  "& svg": {
    fontSize: "20px",
  },
});

export function ViewHeader({ title, onBack, adornment }) {
  return (
    <BackButton onClick={onBack}>
      <Box component="span">
        <KeyboardArrowLeftIcon sx={{ fontSize: "20px", color: "#686868" }} />
      </Box>
      <Typography variant="fw500s16c000000" sx={{ lineHeight: "24px" }}>
        {title}
      </Typography>
      {/* Optional, e.g. an info icon. The whole header is the back button, so a
          click on the adornment must not navigate back. */}
      {adornment && (
        <Box
          component="span"
          sx={{ display: "inline-flex", cursor: "default" }}
          onClick={(e) => e.stopPropagation()}
        >
          {adornment}
        </Box>
      )}
    </BackButton>
  );
}

export function CurrentValueBox({ label, value, sx }) {
  return (
    // sx so a caller can pair two of these in a flex row; full width otherwise.
    <Box sx={{ mb: 3, ...sx }}>
      <Typography
        variant="fw400s14c000000"
        sx={{ mb: 1.5, lineHeight: "24px", color: "#494949", display: "block" }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          backgroundColor: "#F9F9F9",
          borderRadius: "8px",
          padding: "12px 16px",
        }}
      >
        <Typography variant="fw500s14c494949" sx={{ lineHeight: "24px" }}>
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );
}

export function CurrentValuesBox({ label, rows, columns = 1, sx }) {
  // The multi-field sibling of CurrentValueBox: one grey card holding a
  // label/value row per field, so a location (country -> depot) reads as a
  // single current value rather than four stacked boxes the dialog has to
  // scroll past before the user reaches the new-value inputs.
  // columns={2} pairs the rows, which keeps a four-level location two rows tall.
  return (
    <Box sx={{ mb: 2.5, ...sx }}>
      {/* Optional: a caller that heads the card with its own SectionLabel omits it. */}
      {label && (
        <Typography
          variant="fw400s14c000000"
          sx={{ mb: 1.5, lineHeight: "24px", color: "#494949", display: "block" }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          backgroundColor: "#F9F9F9",
          borderRadius: "8px",
          padding: "12px 16px",
          display: "grid",
          gridTemplateColumns:
            columns === 2 ? { xs: "1fr", sm: "1fr 1fr" } : "1fr",
          columnGap: 3,
          rowGap: 0.75,
        }}
      >
        {rows.map(({ label: rowLabel, value }) => (
          <Box
            key={rowLabel}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "#6B7280", flexShrink: 0 }}>
              {rowLabel}
            </Typography>
            {/* The value can outgrow a half-width column (a long depot name, a
                19-digit SIM), so it wraps rather than pushing the card wider. */}
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#2E2E2E",
                textAlign: "right",
                overflowWrap: "anywhere",
              }}
            >
              {value || "N/A"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export function SectionLabel({ children }) {
  // Divider label that separates the current values from the inputs that
  // replace them, so the two halves of the view are unambiguous.
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: "#6B7280",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", backgroundColor: "#F0F0F0" }} />
    </Box>
  );
}

// Pill colours. "critical" marks the admin-only Critical Configuration option
// in the dialog menu; the shape is shared so the two pills read as one family.
const CHIP_TONES = {
  accent: { background: "#EEEFFE", color: "#4A4FD6", border: "#DCDDFB" },
  critical: { background: "#FDECEA", color: "#B50500", border: "#FFCDD2" },
};

export function ChangedChip({ label = "Changed", tone = "accent" }) {
  // CustomTag renders the pill; this only fixes the accent styling, so the two
  // views that mark a replaced value stay identical.
  const colours = CHIP_TONES[tone] ?? CHIP_TONES.accent;
  return (
    <CustomTag
      text={label}
      backgroundColor={colours.background}
      fontColor={colours.color}
      fontSize="10px"
      fontWeight="600"
      containerStyle={{
        ml: 1,
        padding: "0 8px",
        borderRadius: "999px",
        border: `1px solid ${colours.border}`,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    />
  );
}

export function RemarkField({ value, onChange, error, testId, minRows = 4 }) {
  return (
    <Box
      sx={{
        p: 2,
        pl: 3,
        bgcolor: "#F9F9F9",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CustomIcon name="comment-icon" fill="#A4B2C1" sx={{ mt: 0.5 }} />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="fw700s20c000000"
            sx={{ fontSize: "14px", lineHeight: "24px" }}
          >
            Add Final Remark
          </Typography>
          <Typography
            variant="fw700s20c000000"
            sx={{ fontSize: "14px", lineHeight: "24px", color: "#B50500 !important" }}
          >
            &nbsp;*
          </Typography>
        </Box>
      </Box>
      <StyledTextField
        fullWidth
        autoComplete="off"
        variant="outlined"
        placeholder="Type here"
        value={value}
        onChange={onChange}
        multiline
        minRows={minRows}
        error={error || value.length > MAX_REMARK_LENGTH}
        inputProps={testId ? { "data-testid": testId } : undefined}
      />
      {error && (
        <FormHelperText sx={{ fontSize: "10px", color: "#930500", ml: 0, mt: -0.5 }}>
          Please enter a final remark
        </FormHelperText>
      )}
      <Typography
        variant="fw400s10cB50500"
        sx={{
          color:
            value.length > MAX_REMARK_LENGTH
              ? "#930500 !important"
              : "#8D8D8D !important",
        }}
      >
        Only 250 characters are allowed: {value.length}/{MAX_REMARK_LENGTH} characters
      </Typography>
    </Box>
  );
}
